import React from 'react';
import { Award, Target, Heart, Users, TrendingUp, Shield, Leaf, Lightbulb } from 'lucide-react';
import { aboutContent, rawMaterialSuppliers } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const About = () => {
  const valueIcons = {
    'Innovation & Technology Leadership': Lightbulb,
    'Quality & Reliability': Shield,
    'Sustainability & Environmental Stewardship': Leaf,
    'Customer Success & Support': Heart
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-50 via-white to-brand-50 opacity-70"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-200 border-0 mb-4">
              <Award size={14} className="mr-1" />
              Since 2010
            </Badge>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              About <span className="bg-gradient-to-r from-brand-600 to-brand-600 bg-clip-text text-transparent">Vigyan</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Pioneering innovation in paddy processing technology with a commitment to excellence, sustainability, and customer success
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {aboutContent.stats.map((stat, index) => (
              <Card key={index} className="border-0 bg-white shadow-lg hover:shadow-xl transition-all duration-200">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-bold bg-gradient-to-r from-brand-600 to-brand-600 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg flex items-center justify-center">
                    <Target className="text-white" size={24} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {aboutContent.mission}
                </p>
              </div>

              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg flex items-center justify-center">
                    <TrendingUp className="text-white" size={24} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {aboutContent.vision}
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-br from-brand-200 to-brand-200 rounded-full blur-3xl opacity-30"></div>
              <img
                src="https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?q=85"
                alt="Manufacturing Excellence"
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-2xl">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-600 rounded-full flex items-center justify-center">
                    <Users className="text-white" size={28} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">100+</div>
                    <div className="text-sm text-gray-600">Expert Engineers</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-brand-100 text-brand-700 mb-4">
              <Heart size={14} className="mr-1" />
              Our Values
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Drives Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our core values shape everything we do, from design to delivery
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.values.map((value, index) => {
              const IconComponent = valueIcons[value];
              return (
                <Card key={index} className="border-0 bg-white hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 leading-tight">{value}</h3>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Manufacturing Excellence */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1455165814004-1126a7199f9b?q=85"
                alt="CNC Manufacturing"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <div>
                <Badge className="bg-brand-100 text-brand-700 mb-4">
                  <Shield size={14} className="mr-1" />
                  Manufacturing Excellence
                </Badge>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Precision Engineering</h2>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Award className="text-brand-600" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">State-of-the-Art CNC Machines</h3>
                    <p className="text-gray-600">High-precision laser cutting, bending, and MIG welding equipment ensuring superior quality and accuracy in every component.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Shield className="text-brand-600" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Premium Raw Materials</h3>
                    <p className="text-gray-600">Sourcing only the finest stainless steel from industry leaders like SAIL, Jindal, JSW, and Tata Steel.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Users className="text-brand-600" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Engineering Team</h3>
                    <p className="text-gray-600">Dedicated team of skilled engineers and technicians with decades of combined experience in paddy processing technology.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Raw Material Suppliers */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-brand-100 text-brand-700 mb-4">
              <Shield size={14} className="mr-1" />
              Quality Assurance
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Raw Material Partners</h2>
            <p className="text-xl text-gray-600">Trusted partnerships with India's leading steel manufacturers</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {rawMaterialSuppliers.map((supplier, index) => (
              <Card key={index} className="border-2 hover:border-brand-500 hover:shadow-lg transition-all duration-200 bg-white">
                <CardContent className="p-8 flex items-center justify-center">
                  <img 
                    src={supplier.logo} 
                    alt={supplier.name}
                    className="w-full h-24 object-contain"
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-0 bg-gradient-to-br from-brand-600 to-brand-600 text-white shadow-2xl overflow-hidden">
            <CardContent className="p-12 text-center relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <h2 className="text-4xl font-bold mb-4">Join Our Success Story</h2>
                <p className="text-xl text-brand-50 mb-8">
                  Partner with us to transform your paddy processing operations with cutting-edge technology and unmatched support
                </p>
                <a href="/contact" className="inline-block px-8 py-4 bg-white text-brand-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-xl">
                  Get Started Today
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default About;